import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import IntegrationAuthProvider from '../../../IntegrationAuthProvider/IntegrationAuthProvider'
import Loading from '../../../../molecules/Loading'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import SydButton from '../../../../commonDesign/Button'
import NotLinkedInline from '../../../WealthboxClientLink/NotLinkedInline'
import SmartComponentDisplayName from '../../SmartComponentDisplayName'
import useWealthboxContactInfo from './useWealthboxContactInfo'
import WealthboxContactPanel from './WealthboxContactPanel'

const SIZE_STYLES = {
  sm: { minHeight: '80px' },
  md: { minHeight: '100px' },
  lg: { minHeight: '120px' }
}

function WealthboxContactInfoInner ({ config, styleVariant: sizeVariant }) {
  const sizes = SIZE_STYLES[sizeVariant] || SIZE_STYLES.md
  const enableEdit = config?.enableEdit ?? false
  const title = config?.hideTitle ? null : (config?.title ?? 'Family')
  const icon = config?.hideIcon ? null : (config?.icon ?? 'wealthboxSmall')
  const visibleFields = config?.visibleContactFields || []

  const [selected, setSelected] = useState(null) // { contactId, name }
  const { buttons, isLoading, notLinked } = useWealthboxContactInfo(config)

  const handleClick = useCallback((btn) => setSelected({ contactId: btn.contactId, name: btn.name }), [])
  const handleClose = useCallback(() => setSelected(null), [])

  if (isLoading) return <Loading />
  if (notLinked) return <FadeIn><NotLinkedInline minHeight={sizes.minHeight} /></FadeIn>

  if (!buttons.length) {
    return (
      <div className='p-md font-size-sm' style={{ color: 'var(--text-color-subtle)' }}>
        No contacts to display.
      </div>
    )
  }

  return (
    <FadeIn>
      <div style={{ padding: '8px 12px' }}>
        {title || icon ? (
          <div style={{ marginBottom: '8px' }}>
            <SmartComponentDisplayName title={title} icon={icon} size={sizeVariant} />
          </div>
        ) : null}

        <div className='flex items-center gap-2' style={{ flexWrap: 'wrap' }}>
          {buttons.map(btn => (
            <SydButton
              key={btn.id}
              size='sm'
              variant={btn.isPrimary ? 'primary' : 'outline'}
              onClick={() => handleClick(btn)}
            >
              {btn.name}
              {btn.title ? (
                <span style={{ marginLeft: '6px', opacity: 0.7, fontSize: 'var(--font-size-xs)' }}>
                  · {btn.title}
                </span>
              ) : null}
            </SydButton>
          ))}
        </div>

        <WealthboxContactPanel
          open={!!selected}
          onClose={handleClose}
          contactId={selected?.contactId}
          contactName={selected?.name}
          enableEdit={enableEdit}
          visibleFields={visibleFields}
        />
      </div>
    </FadeIn>
  )
}
WealthboxContactInfoInner.propTypes = {
  config: PropTypes.object,
  styleVariant: PropTypes.string
}

function WealthboxContactInfoRenderer ({ config, styleVariant, presentationKey, blueprint, displayName }) {
  const sizes = SIZE_STYLES[styleVariant] || SIZE_STYLES.md
  return (
    <div style={{ minHeight: sizes.minHeight }}>
      <IntegrationAuthProvider integration='wealthbox' size='sm' loadingType='circular'>
        <WealthboxContactInfoInner
          config={config}
          styleVariant={styleVariant}
          presentationKey={presentationKey}
          blueprint={blueprint}
          displayName={displayName}
        />
      </IntegrationAuthProvider>
    </div>
  )
}
WealthboxContactInfoRenderer.propTypes = {
  config: PropTypes.object,
  styleVariant: PropTypes.string,
  presentationKey: PropTypes.string,
  blueprint: PropTypes.object,
  displayName: PropTypes.string
}

export default WealthboxContactInfoRenderer
